package dev.lingvo.site.pages.app.projects

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.ProjectSummaryDto
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.api.Async
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LArrowRightIcon
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LPlusIcon
import dev.lingvo.site.ds.LSearchField
import dev.lingvo.site.ds.LVisuallyHiddenStyle
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.pages.app.ProjectCard
import dev.lingvo.site.pages.app.ProjectGridStyle
import dev.lingvo.site.state.ProjectDirectory
import dev.lingvo.site.state.WorkspaceRequestContext
import dev.lingvo.site.state.WorkspaceRequestKind
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Page("/app/projects")
@Layout(".components.layouts.AppLayout")
@Composable
fun ProjectsPage() {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    // Global search routes here with ?search= when no project is open; ignoring it meant the search
    // box appeared to do nothing from the dashboard.
    val initialQuery = ctx.route.queryParams["search"].orEmpty()
    var query by remember(initialQuery) { mutableStateOf(initialQuery) }
    val context = WorkspaceRequestContext.current()
    context?.let { ProjectDirectory.ensure(scope, it) }
    val canCreate = context?.canManageProjects == true &&
        context.decision(WorkspaceRequestKind.CREATE_PROJECT).allowed

    Column(Modifier.fillMaxWidth().gap(Space.XXL)) {
        PageHeader(
            title = t("app.projects.index.projects", "Projects"),
            subtitle = t("app.projects.index.every_project_in_this_workspace_newest", "Every project in this workspace, newest activity first."),
        ) {
            ProjectsHeaderActions(
                canCreate = canCreate,
                projects = ProjectDirectory.projects,
                onNewProject = { ctx.router.tryRoutingTo("/app/projects/new") },
            )
        }
        when {
            // The slot was `{}`, which is the dead end `Activity.kt` documents having fixed for
            // the same state: `WorkspaceSelection` falls back to the strongest membership, so this
            // reader is in no workspace at all and "join or create one" named no way to do either.
            context == null -> LEmptyState(
                title = t("app.projects.index.no_workspace_selected", "No workspace selected"),
                description = t("app.projects.index.a_workspace_holds_the_projects_this_2", "A workspace holds the projects this list is about. Create one, or ask " +
                    "whoever invited you to send the invitation again."),
            ) {
                LButton(
                    label = t("app.projects.index.create_a_workspace", "Create a workspace"),
                    onClick = { ctx.router.tryRoutingTo("/app/workspaces") },
                )
            }
            ProjectDirectory.loading && ProjectDirectory.projects.isEmpty() ->
                LoadingState(rows = 4, label = t("app.projects.index.loading_projects", "Loading projects"))
            ProjectDirectory.error != null && ProjectDirectory.projects.isEmpty() -> AsyncFailureState(
                // The status travels. Without it `AsyncFailureState`'s refusal and missing branches
                // are unreachable — `isRefusal` and `isMissing` are derived from it — so a reader
                // removed from the workspace, or one whose workspace was renamed, met a red fault
                // and a Retry button that re-ran the request the server had already refused. This
                // exact line was changed to `AsyncFailureState` for that reason and a later refactor
                // rewrapped a bare message, which quietly restored the behaviour it removed.
                failure = Async.Failure(ProjectDirectory.error.orEmpty(), ProjectDirectory.errorStatus),
                subject = t("app.projects.index.this_workspaces_projects", "this workspace's projects"),
                onRetry = { ProjectDirectory.ensure(scope, context, force = true) },
            )
            else -> {
                ProjectDirectory.error?.let { message ->
                    LAlert(t("app.projects.index.projects_could_not_be_refreshed", "Projects could not be refreshed"), message, variant = LAlertVariant.WARNING)
                }
                ProjectsContent(
                    projects = ProjectDirectory.projects,
                    query = query,
                    onQueryChange = { query = it },
                    onOpen = { slug -> ctx.router.tryRoutingTo("/app/projects/$slug") },
                    onNavigate = { path -> ctx.router.tryRoutingTo(path) },
                    onNewProject = { ctx.router.tryRoutingTo("/app/projects/new") },
                    onStart = { ctx.router.tryRoutingTo("/app/start") },
                    canCreate = canCreate,
                )
            }
        }
    }
}

/**
 * The header's door to a new project, which the empty state below also offers.
 *
 * Its own composable because the decision is about the *pair*: whether this button exists at all
 * depends on what the list underneath it is showing, and a rule buried in a `PageHeader` lambda
 * cannot be read beside the control it duplicates.
 */
@Composable
internal fun ProjectsHeaderActions(
    canCreate: Boolean,
    projects: List<ProjectSummaryDto>,
    onNewProject: () -> Unit,
) {
    // **Only when there is a list to add to.** An empty workspace already draws this invitation in
    // the empty state below, with the sentence that explains it and the "Other ways to start" door
    // beside it; drawing a second accent-filled "New project" up here made the same verb, the same
    // route and the same accent compete with itself on the one screen where a reader has exactly
    // one thing to do.
    if (canCreate && projects.isNotEmpty()) {
        LButton(
            label = t("app.projects.index.new_project", "New project"),
            onClick = onNewProject,
            leadingIcon = { LPlusIcon() },
        )
    }
}

@Composable
internal fun ProjectsContent(
    projects: List<ProjectSummaryDto>,
    query: String,
    onQueryChange: (String) -> Unit,
    onOpen: (String) -> Unit,
    onNavigate: (String) -> Unit,
    onNewProject: () -> Unit,
    onStart: () -> Unit,
    canCreate: Boolean,
) {
    if (projects.isEmpty()) {
        LEmptyState(
            t("app.projects.index.no_projects_yet", "No projects yet"),
            // Branched, because the action slot is: a translator was told to create a project and
            // then shown no button, with nothing accounting for its absence. `Keys.kt` answers the
            // same state by branching both halves.
            if (canCreate) {
                t("app.projects.index.create_a_project_then_push_your", "Create a project, then push your source strings with the web importer or the CLI.")
            } else {
                t("app.projects.index.an_owner_or_admin_needs_to", "An owner or admin needs to create the first project here before there is anything to translate.")
            },
            // The three doors, from the one screen that has none. Somebody arriving here with a
            // repository or a terminal does not want the blank-project form, and `/app/start` was
            // reachable only from the dashboard's first run and the palette.
            secondaryAction = {
                LButton(
                    t("app.projects.index.other_ways_to_start", "Other ways to start"),
                    onStart,
                    variant = LButtonVariant.GHOST,
                    trailingIcon = { LArrowRightIcon() },
                )
            },
        ) {
            if (canCreate) {
                LButton(t("app.projects.index.new_project", "New project"), onNewProject, leadingIcon = { LPlusIcon() })
            }
        }
        return
    }

    val visible = projects.filter { project -> project.matchesQuery(query) }

    // **The entrance belongs to the page arriving, not to the filter.**
    //
    // Compose identifies these cards positionally. Narrowing the query destroys the ones that no
    // longer match, and every keystroke that widens it again builds fresh nodes — each starting a
    // new `lingvo-enter` with its own stagger delay, so backspacing reassembled the whole grid
    // under the caret. A query matching nothing is worse: the grid `Div` is replaced by the "no
    // match" line, and the next keystroke that matches anything rebuilds it and replays the
    // cascade from the first card.
    //
    // So the first edit retires the entrance for the rest of the visit — the `drawnOnce` gate
    // `LRouteTransition` already uses, in the one place a list here is re-keyed by typing. It is
    // keyed off the query as it was on arrival rather than off blankness, because otherwise
    // clearing the box back to empty would re-arm it and restage every card on screen at once.
    val arrivalQuery = remember { query }
    var refiled by remember { mutableStateOf(false) }
    LaunchedEffect(query) { if (query != arrivalQuery) refiled = true }

    Column(Modifier.fillMaxWidth().gap(Space.LG)) {
        LSearchField(
            value = query,
            onValueChange = onQueryChange,
            modifier = Modifier.maxWidth(420.px),
            label = t("app.projects.index.filter_projects", "Filter projects"),
            placeholder = t("app.projects.index.filter_by_name_slug_or_language", "Filter by name, slug, or language"),
            shortcutHint = null,
        )
        // **One region, outside the branch.** Both cases used to carry their own `role="status"`,
        // in arms of an `if` that swap DOM nodes — and a live region inserted in the same update as
        // its text is one nothing announces. `LCopyField` states that rule in its own words. So the
        // first keystroke that emptied the list was silent, and so was the one that filled it again;
        // only keystrokes that kept the list non-empty announced, which is why it looks fine to try.
        Span(
            LVisuallyHiddenStyle.toModifier().toAttrs {
                attr("role", "status")
                attr("aria-atomic", "true")
            },
        ) {
            Text(
                if (visible.isEmpty()) {
                    tf("app.projects.index.no_match_status", "No project matches \"{query}\".", "query" to query)
                } else if (query.isBlank()) {
                    tPlural(
                        "app.projects.index.count.project",
                        visible.size,
                        "{count} project",
                        "{count} projects",
                    )
                } else {
                    // The query stays outside the counted form: a translated form must not have to
                    // carry the reader's own search text.
                    tPlural(
                        "app.projects.index.count.project_match",
                        visible.size,
                        "{count} project matches",
                        "{count} projects match",
                    ) + " \"$query\""
                },
            )
        }
        if (visible.isEmpty()) {
            P(
                Modifier
                    .color(ColorMode.current.toSitePalette().textSecondary)
                    .fontSize(TypeScale.BodySmall)
                    .margin(0.px)
                    .toAttrs(),
            ) {
                Text(tf("app.projects.index.no_match_note", "No project matches \"{query}\".", "query" to query))
            }
        } else {
            Div(ProjectGridStyle.toModifier().toAttrs()) {
                visible.forEachIndexed { index, project ->
                    ProjectCard(
                        project = project,
                        lastActivity = null,
                        onOpen = { onOpen(project.slug) },
                        onNavigate = onNavigate,
                        index = if (refiled) null else index,
                    )
                }
            }
        }
    }
}

/**
 * Whether a project should stay visible for [query].
 *
 * Matches the slug and the project's languages as well as its name — with a dozen projects the
 * fastest way to find one is usually "the Japanese app", not its exact title. Extracted from the
 * composable so it can be tested without a DOM.
 */
internal fun ProjectSummaryDto.matchesQuery(query: String): Boolean {
    val needle = query.trim().lowercase()
    if (needle.isEmpty()) return true
    if (name.lowercase().contains(needle) || slug.lowercase().contains(needle)) return true
    return locales.any { locale ->
        val info = localeInfo(locale)
        locale.lowercase().contains(needle) ||
            info.englishName.lowercase().contains(needle) ||
            info.nativeName.lowercase().contains(needle)
    }
}
